import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Select, SelectOption, SelectValue, TableRowData } from 'tdesign-react';

import { IBot, ISpace } from '@/interface';
import { Bot, IPromotionItemInput, OthersData } from '@/interface/promotion';

import { ChangeDataType, fetchBotOptions, formKeys } from '../custom-index/config';
import styles from './index.module.less';
import List from './list';

export const CustomBotComponent: React.FC<{
  value: Bot;
  parentData?: IPromotionItemInput;
  onChange: (key: Bot) => void;
}> = (props) => {
  const { parentData, onChange } = props || {};
  const values = parentData?.bots || props.value;
  const [compValue, setCompValue] = useState<Bot['bots']>([]);
  const [botOptions, setBotOptions] = useState<SelectOption[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const spaceId = parentData?.space?.id || (parentData?.space as { opt?: ISpace })?.opt?.id;

  const loadBotOptions = async (keyword?: string, resetPage = false) => {
    if (loading) return;
    setLoading(true);
    const currentPage = resetPage ? 1 : page;
    const res = await fetchBotOptions({
      spaceId,
      name: keyword ?? searchValue,
      page: currentPage,
      size: 100,
    });

    let listOptions: any[] = [];
    if (resetPage) {
      listOptions = res.list;
    } else {
      listOptions = [...botOptions, ...res.list];
    }
    setBotOptions(listOptions?.map(e => ({
      value: e?.value,
      label: `${e?.label}（${e?.value}）`,
      opt: e?.opt,
    })));
    setHasMore(res.hasMore);
    setLoading(false);
    setPage(currentPage + 1);
  };

  useEffect(() => {
    loadBotOptions();
  }, [spaceId]);

  useEffect(() => {
    setCompValue(values?.bots);
  }, [values]);

  const handleSearch = _.debounce((keyword: string) => {
    setSearchValue(keyword);
    loadBotOptions(keyword, true);
  }, 500);

  const handleScrollToBottom = () => {
    if (!loading && hasMore) {
      loadBotOptions(searchValue);
    }
  };

  const moveBot = (id: string) => {
    const bots = compValue ? [...compValue] : [];
    const ids = bots.map(item => item.id);
    const index = ids.findIndex(item => item === id);
    bots.splice(index, 1);
    onChange({ ...values, bots });
  };

  const listDataChange = (data: TableRowData) => {
    onChange({ ...values, [formKeys.bots]: data });
  };

  const othersChange = (data: OthersData[]) => {
    props.onChange({ ...props.value, [formKeys.others]: data });
  };

  const changeData = (type: number, values?: Bot['bots']) => {
    if (type === ChangeDataType.INPUT) {
      return (values)?.map(e => (e as unknown as { opt: IBot }).opt);
    }
    if (type === ChangeDataType.OUTPUT) {
      return values?.map(e => ({ label: e?.name, value: e?.id, opt: e }));
    }
  };

  const handleChange = (value: SelectValue) => {
    const tempValue = (changeData(ChangeDataType.INPUT, value as IBot[])) as IBot[];
    onChange({ ...values, [formKeys.bots]: tempValue });
  };

  const bots = changeData(ChangeDataType.OUTPUT, compValue);

  return (
    <div className={styles.wrapper}>
      <Select
        className={styles.botSelect}
        value={bots}
        filterable
        onChange={value => handleChange(value)}
        options={botOptions}
        disabled={false}
        multiple={true}
        valueType='object'
        onSearch={handleSearch}
        popupProps={{ onScrollToBottom: handleScrollToBottom }}
      />
      <div className={styles.table}>
        <List
          data={compValue}
          othersData={props.value?.others}
          moveBot={moveBot}
          onChange={listDataChange}
          othersChange={othersChange}
        />
      </div>
    </div>
  );
};
